import { fromPromise } from 'xstate';
import { apolloClient } from '@/lib/apollo';
import {
  ChooseDirectionMutation,
  ChooseRouteMutation,
  ChooseStopMutation,
  RefreshScheduledBusesMutation,
  ViewScheduledBusesQuery,
} from '@/lib/gql/__generated__/canonical-documents';
import type {
  ChooseDirectionInput,
  ChooseRouteInput,
  ChooseStopInput,
  RefreshScheduledBusesInput,
  ViewScheduledBusesQueryQuery,
} from '@/lib/gql/__generated__/graphql';

const mutationMessage = (message: string | null | undefined) => message ?? 'The request could not be completed.';

export const viewScheduledBusesActor = fromPromise<ViewScheduledBusesQueryQuery['viewScheduledBuses'], void>(
  async () => {
    const result = await apolloClient.query({ query: ViewScheduledBusesQuery });
    if (!result.data) throw new Error('Scheduled buses could not be loaded.');
    return result.data.viewScheduledBuses;
  },
);

export const chooseRouteActor = fromPromise<string, Omit<ChooseRouteInput, 'stopSelectionId'>>(async ({ input }) => {
  const stopSelectionId = crypto.randomUUID();
  const result = await apolloClient.mutate({
    mutation: ChooseRouteMutation,
    variables: { input: { scheduleId: input.scheduleId, routeId: input.routeId, stopSelectionId } },
  });
  const response = result.data?.chooseRoute;
  if (!response?.success) throw new Error(mutationMessage(response?.error?.message));
  return stopSelectionId;
});

export const chooseDirectionActor = fromPromise<void, ChooseDirectionInput>(async ({ input }) => {
  const result = await apolloClient.mutate({
    mutation: ChooseDirectionMutation,
    variables: { input: { stopSelectionId: input.stopSelectionId, directionId: input.directionId } },
  });
  const response = result.data?.chooseDirection;
  if (!response?.success) throw new Error(mutationMessage(response?.error?.message));
});

export const chooseStopActor = fromPromise<void, ChooseStopInput>(async ({ input }) => {
  const result = await apolloClient.mutate({
    mutation: ChooseStopMutation,
    variables: { input: { stopSelectionId: input.stopSelectionId, stopId: input.stopId } },
  });
  const response = result.data?.chooseStop;
  if (!response?.success) throw new Error(mutationMessage(response?.error?.message));
});

export const refreshScheduledBusesActor = fromPromise<void, RefreshScheduledBusesInput>(async ({ input }) => {
  const result = await apolloClient.mutate({
    mutation: RefreshScheduledBusesMutation,
    variables: { input: { stopSelectionId: input.stopSelectionId } },
  });
  const response = result.data?.refreshScheduledBuses;
  if (!response?.success) throw new Error(mutationMessage(response?.error?.message));
});
